<template>
  <div class="agentic-dashboard-wrapper">
    <ErrorPopup />
    <!-- Dashboard Header -->
    <div class="dashboard-header">
      <div class="dashboard-header-left">
        <div class="dash-top-center">
          <input 
            type="text" 
            v-model="dashboardName" 
            :style="{ width: inputWidth + 'ch' }" 
            placeholder="untitled"
            @change="handleDashboardNameChange"
            class="dashboard-name-input"
          />
        </div>
      </div>
      <div class="dashboard-header-right">
        <button 
          class="chat-toggle-btn" 
          @click="toggleChat"
          :class="{ 'active': chatVisible }"
          :title="chatVisible ? 'Hide Chat' : 'Show Chat'"
        >
          <img :src="chatIcon" alt="chat" />
        </button>
        <button 
          class="dash-save" 
          @click="saveDashboard"
          :disabled="!isDashboardNameChanged || saving"
          :class="{ 'disabled': !isDashboardNameChanged || saving }"
        >
          <img :src="saveIcon" alt="save" />
        </button>
        <button class="dash-delete" @click="showDeleteDialog = true">
          <img :src="deleteIcon" alt="delete" />
        </button>
      </div>
    </div>

    <div class="agentic-dashboard-content" :class="{ 'plot-selected': selectedChartIndex !== null }">
      <!-- Left side: Selected plot view (when plot is selected) -->
      <div v-if="selectedChartIndex !== null && charts[selectedChartIndex]" class="selected-plot-panel">
        <div class="selected-plot-header">
          <h6 class="selected-plot-title">{{ getChartTitle(selectedChartIndex) }}</h6>
          <div class="selected-plot-actions">
            <button class="chart-action-btn-in-header" @click.stop="handleChartAction(selectedChartIndex)">
              <span class="action-label">Edit in Studio</span>
            </button>
            <button class="close-plot-btn" @click="deselectPlot" title="Close">
              <v-icon size="20">mdi-close</v-icon>
            </button>
          </div>
        </div>
        <div class="selected-plot-content">
          <ChartCard
            :chart-title="getChartTitle(selectedChartIndex)"
            :chart-data="charts[selectedChartIndex]"
            :chart-index="selectedChartIndex"
            :is-selected="true"
            :full-screen="false"
            @chart-action="handleChartAction"
          />
        </div>
      </div>

      <!-- Main dashboard section with charts grid (6 slots) - shown when no plot is selected -->
      <div v-if="selectedChartIndex === null" class="dashboard-main-section">
        <div class="dashboard-inner" style="position: relative;">
          <div v-if="loadingCharts" class="charts-loading-overlay">
            <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
            <p class="loading-text">Loading plots...</p>
          </div>
          <DashboardGrid
            :charts="charts"
            :selected-chart-index="selectedChartIndex"
            @chart-action="handleChartAction"
            @chart-selected="handleChartSelected"
            @empty-slot-click="handleEmptySlotClick"
          />
        </div>
      </div>
      
      <!-- Right sidebar with chat or visual edit -->
      <div class="dashboard-sidebar" :class="{ 'hidden': !chatVisible && !showVisualEditPanel }">
        <!-- Chat Sidebar -->
        <ChatSidebar
          v-if="!showVisualEditPanel"
          ref="chatSidebar"
          :messages="currentChatMessages"
          :initial-message="initialMessage"
          :ai-response="aiResponse"
          :selected-chart-index="selectedChartIndex"
          :loading="loadingChatMessage"
          @message-like="handleMessageLike"
          @message-dislike="handleMessageDislike"
          @send-message="handleChatMessage"
          @visual-edit="handleVisualEdit"
        />
        <!-- Visual Edit Panel (replaces chat) -->
        <div v-if="showVisualEditPanel && selectedChartIndex !== null" class="visual-edit-sidebar">
          <div class="visual-edit-sidebar-header">
            <h6 class="visual-edit-sidebar-title">Visual Edit</h6>
            <div class="visual-edit-sidebar-actions">
              <button class="switch-to-chat-btn" @click="switchToChat" title="Switch to Chat">
                <v-icon size="20">mdi-message-text</v-icon>
              </button>
              <button class="close-edit-btn" @click="exitEditMode" title="Close">
                <v-icon size="20">mdi-close</v-icon>
              </button>
            </div>
          </div>
          <div class="visual-edit-sidebar-content">
            <VisualEditsPanel
              v-if="charts[selectedChartIndex]"
              :key="`visual-edit-${selectedChartIndex}`"
              :selected-chart="charts[selectedChartIndex]"
              @plot-updated="handlePlotUpdated"
              @close="exitEditMode"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      transition="dialog-top-transition"
      class="save-dashboard"
      width="600"
      v-model="showDeleteDialog"
    >
      <template v-slot:default="{ isActive }">
        <v-card>
          <v-card-title class="d-flex align-center justify-between">
            <v-spacer></v-spacer>
            <v-btn
              @click="showDeleteDialog = false"
              flat
              icon
              class="chart-preview-close-btn"
              size="x-small"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="mt-4 mx-5">
            <h6 class="text-center">Delete Dashboard</h6>
            <p class="text-center">
              Are you sure you want to delete dashboard?
            </p>
          </v-card-text>
          <v-card-actions class="btn-wrap">
            <v-btn class="cancel-btn" @click="isActive.value = false">
              <span>Cancel</span>
            </v-btn>
            <v-btn
              class="delete-btn"
              @click="confirmDelete"
              :loading="deleting"
            >
              <img class="mr-2" :src="deleteDashboardPopup" width="15px" height="15px" alt="delete_dashboard" />
              <span>Delete</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </div>
</template>

<script>
import DashboardGrid from "@/components/AgenticDashboard/DashboardGrid.vue";
import ChartCard from "@/components/AgenticDashboard/ChartCard.vue";
import ChatSidebar from "@/components/AgenticDashboard/ChatSidebar.vue";
import VisualEditsPanel from "@/components/AgenticDashboard/VisualEditsPanel.vue";
import AgenticSidebar from "@/components/AgenticDashboard/AgenticSidebar.vue";
import ErrorPopup from "@/components/layout/Popups/ErrorPopup.vue";
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";
import Plotly from "plotly.js-dist-min";

export default {
  name: "AgenticDashboardView",
  components: {
    DashboardGrid,
    ChartCard,
    ChatSidebar,
    VisualEditsPanel,
    ErrorPopup,
  },
  props: {
    dashboardId: {
      type: [String, Number],
      default: null,
    },
    dashboardName: {
      type: String,
      default: null,
    },
    initialMessage: {
      type: String,
      default: null,
    },
    dashboardData: {
      type: Object,
      default: null,
    },
  },
  data() {
    // Initialize dashboard name from URL if available (for refresh scenarios)
    const routeName = this.$route?.params?.dashboardName;
    let initialName = "Untitled Dashboard";
    if (routeName) {
      try {
        // Explicitly decode to convert %20 to spaces
        initialName = decodeURIComponent(routeName);
      } catch (e) {
        // If already decoded or invalid, use as is
        initialName = routeName;
      }
    }
    
    return {
      charts: Array(6).fill(null), // 6 slots for plots
      dashboardName: initialName,
      originalDashboardName: initialName, // Track original name to detect changes
      dashboardIdValue: null, // Store the actual dashboard ID
      chatVisible: true, // Chat sidebar visibility toggle
      showDeleteDialog: false,
      deleting: false,
      saving: false,
      selectedChartIndex: null, // Currently selected chart index
      showVisualEditPanel: false, // Show visual edit panel (replaces chat)
      deleteIcon: require("@/assets/Svg/Dashboard/delete.svg"),
      deleteDashboardPopup: require("@/assets/Svg/Chart/delete-white.svg"),
      saveIcon: require("@/assets/Svg/save.svg"),
      chatIcon: require("@/assets/Images/FDPal/send-2.svg"), // Chat toggle icon
      aiResponse: null,
      loadingCharts: false,
      loadingChatMessage: false, // Loading state for chat messages
      // Session-only chat history: key = "dashboard" or chart ID; cleared on refresh/leave
      chatHistories: {},
      dashboardAccessLevel: "private", // Default access level
      isDefault: false, // Default dashboard flag
    };
  },
  computed: {
    tenantId() {
      return this.$route.path.split("/")[1] || getTenantPrefix();
    },
    inputWidth() {
      return this.dashboardName.length > 0 ? this.dashboardName.length : 15;
    },
    // Key for current chat context: "dashboard" or selected chart ID
    currentChatKey() {
      if (this.selectedChartIndex !== null && this.charts[this.selectedChartIndex]) {
        const chart = this.charts[this.selectedChartIndex];
        const id = chart?.data?.ID || chart?.ID;
        return id ? String(id) : "dashboard";
      }
      return "dashboard";
    },
    // Messages for the current context (dashboard or single-plot chat); persists for session only
    currentChatMessages() {
      const list = this.chatHistories[this.currentChatKey];
      return Array.isArray(list) ? list : [];
    },
    currentDashboardId() {
      // Get dashboard ID from stored value, prop, route params, or dashboardData
      if (this.dashboardIdValue) {
        return this.dashboardIdValue;
      }
      if (this.dashboardId) {
        return this.dashboardId;
      }
      if (this.$route.params.dashboardId) {
        return this.$route.params.dashboardId;
      }
      if (this.dashboardData && this.dashboardData.ID) {
        return this.dashboardData.ID;
      }
      return null;
    },
    isDashboardNameChanged() {
      // Check if dashboard name has changed from original
      return this.dashboardName.trim() !== this.originalDashboardName.trim() && 
             this.dashboardName.trim() !== "";
    },
  },
  mounted() {
    this.initializeDashboard();
  },
  watch: {
    // Watch for route changes (e.g., when user navigates to a different dashboard)
    '$route.params.dashboardName'(newName, oldName) {
      if (newName && newName !== oldName) {
        console.log("Dashboard name changed in route, loading dashboard:", newName);
        this.initializeDashboard();
      }
    },
    // Watch for dashboardName prop changes
    dashboardName(newName, oldName) {
      if (newName && newName !== oldName) {
        console.log("Dashboard name prop changed, loading dashboard:", newName);
        this.initializeDashboard();
      }
    },
    // Watch for dashboardId prop changes (backward compatibility)
    dashboardId(newId, oldId) {
      if (newId && newId !== oldId) {
        console.log("Dashboard ID prop changed, loading dashboard:", newId);
        this.initializeDashboard();
      }
    },
    // Watch for chat visibility changes and resize charts
    chatVisible(newVal, oldVal) {
      if (newVal !== oldVal) {
        // Wait for CSS transition to complete (0.3s) before resizing
        setTimeout(() => {
          this.resizeAllCharts();
        }, 350); // Slightly longer than CSS transition (300ms)
      }
    },
    // Seed dashboard chat history with initial message when present (e.g. from home page)
    initialMessage: {
      handler(val) {
        if (!val || this.currentChatKey !== "dashboard") return;
        this.ensureChatHistory("dashboard");
        const list = this.chatHistories.dashboard;
        const exists = list.some((m) => m.role === "user" && m.text === val);
        if (!exists) list.push({ role: "user", text: val });
      },
      immediate: true,
    },
  },
  methods: {
    initializeDashboard() {
    // Check if we have dashboard data from API response (from prop or query params)
    const dashboardData = this.dashboardData || (() => {
      const dashboardDataStr = this.$route.query.dashboardData;
      if (dashboardDataStr) {
        try {
          return JSON.parse(dashboardDataStr);
        } catch (error) {
          console.error("Error parsing dashboard data:", error);
          return null;
        }
      }
      return null;
    })();

      // Get dashboard name from URL route params FIRST (for refresh scenarios)
      // This ensures we always use the URL, not the input field value
      let dashboardName = this.$route.params.dashboardName || this.dashboardName;
      // Get dashboard ID from prop or route params (backward compatibility)
      const dashboardId = this.dashboardId || this.$route.params.dashboardId;

    if (dashboardData) {
      this.handleDashboardApiResponse(dashboardData);
      } else if (dashboardName) {
        // Ensure the name from URL is properly decoded
        if (this.$route.params.dashboardName) {
          // Name is from URL - ensure it's decoded (Vue Router should decode, but double-check)
          try {
            dashboardName = decodeURIComponent(this.$route.params.dashboardName);
          } catch (e) {
            // If already decoded or invalid, use as is
            dashboardName = this.$route.params.dashboardName;
          }
        } else {
          // Name is from prop or data, ensure it's decoded
          try {
            dashboardName = decodeURIComponent(dashboardName);
          } catch (e) {
            // Already decoded or not encoded
          }
        }
        console.log("Loading dashboard from name (from URL):", dashboardName);
        console.log("Route params dashboardName (raw):", this.$route.params.dashboardName);
        console.log("Current dashboardName in data:", this.dashboardName);
        // Use the name from URL, not from input field
        this.loadDashboardByName(dashboardName);
      } else if (dashboardId) {
        console.log("Loading dashboard from ID:", dashboardId);
        this.loadDashboard(dashboardId);
    } else if (this.initialMessage) {
      // If we have an initial message but no dashboard ID, create a new dashboard
      this.createDashboardFromMessage(this.initialMessage);
    }
  },
    async loadDashboardByName(name) {
      if (!name) {
        console.error("Dashboard name is required");
        this.$store.commit('api/showSnackbar', {
          message: "Dashboard name is missing",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      try {
        // Load dashboard data by name
        // The API expects the name to be JSON-stringified (with quotes) and then URL encoded
        // Example: name="Untitled 88" -> JSON.stringify -> "\"Untitled 88\"" -> encodeURIComponent -> "%22Untitled%2088%22"
        const jsonStringifiedName = JSON.stringify(name);
        const encodedName = encodeURIComponent(jsonStringifiedName);
        console.log("Fetching dashboard with name:", name);
        console.log("JSON stringified:", jsonStringifiedName);
        console.log("URL encoded:", encodedName);
        console.log("Full API URL:", `public/api/dashboards?name=${encodedName}`);
        const response = await api.get(`public/api/dashboards?name=${encodedName}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });

        if (
          response &&
          response.data &&
          (response.data.statusCode === 200 || response.data.statusCode === 201)
        ) {
          const dashboards = response.data.data.dashboards || [];
          if (dashboards.length === 0) {
            throw new Error("Dashboard not found");
          }
          
          const dashboard = dashboards[0];
          const fetchedName = dashboard.name || dashboard.Name || "Untitled Dashboard";
          
          // Store the actual dashboard ID
          this.dashboardIdValue = dashboard.ID || dashboard.id || null;
          
          // Update dashboardName from API response (this will update the input field)
          this.dashboardName = fetchedName;
          this.originalDashboardName = fetchedName; // Set original name
          this.dashboardAccessLevel = dashboard.AccessLevel || "private";
          this.isDefault = dashboard.isDefault || false;

          // Update URL with dashboard name if it's different
          // Vue Router will handle encoding automatically
          const currentName = this.$route.params.dashboardName;
          if (currentName !== this.dashboardName) {
            this.$router.replace({
              name: "AgenticDashboardView",
              params: { dashboardName: this.dashboardName },
            });
          }

          // Load charts from ChartsID array (same as handleDashboardApiResponse)
          if (dashboard.ChartsID && dashboard.ChartsID.length > 0) {
            this.loadingCharts = true;
            await this.loadChartsFromIds(dashboard.ChartsID);
            this.loadingCharts = false;
            this.$store.commit('api/showSnackbar', {
              message: "Dashboard loaded successfully",
              color: 'success',
              timeout: 3000
            });
          } else {
            // Reset charts array if no charts
            this.charts = Array(6).fill(null);
          }
        } else {
          throw new Error(response?.data?.message || "Failed to load dashboard");
        }
      } catch (error) {
        console.error("Error loading dashboard:", error);
        this.$store.commit('api/showSnackbar', {
          message: error.response?.data?.message || "Failed to load dashboard",
          color: 'error',
          timeout: 5000
        });
      }
    },
    async loadDashboard(id) {
      if (!id) {
        console.error("Dashboard ID is required");
        this.$store.commit('api/showSnackbar', {
          message: "Dashboard ID is missing",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      try {
        // Load dashboard data by ID (backward compatibility)
        const response = await api.get(`public/api/dashboards/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });

          if (
            response &&
            response.data &&
            (response.data.statusCode === 200 || response.data.statusCode === 201)
          ) {
            const dashboard = response.data.data.dashboards[0];
          
          // Store the actual dashboard ID
          this.dashboardIdValue = dashboard.ID || dashboard.id || null;
          
          this.dashboardName = dashboard.name || dashboard.Name || "Untitled Dashboard";
          this.originalDashboardName = this.dashboardName; // Set original name
          this.dashboardAccessLevel = dashboard.AccessLevel || "private";
          this.isDefault = dashboard.isDefault || false;

          // Update URL with dashboard name
          // Vue Router will handle encoding automatically
          this.$router.replace({
            name: "AgenticDashboardView",
            params: { dashboardName: this.dashboardName },
          });

          // Load charts from ChartsID array (same as handleDashboardApiResponse)
          if (dashboard.ChartsID && dashboard.ChartsID.length > 0) {
            this.loadingCharts = true;
            await this.loadChartsFromIds(dashboard.ChartsID);
            this.loadingCharts = false;
            this.$store.commit('api/showSnackbar', {
              message: "Dashboard loaded successfully",
              color: 'success',
              timeout: 3000
            });
          } else {
            // Reset charts array if no charts
            this.charts = Array(6).fill(null);
          }
        } else {
          throw new Error(response?.data?.message || "Failed to load dashboard");
        }
      } catch (error) {
          console.error("Error loading dashboard:", error);
        this.$store.commit('api/showSnackbar', {
          message: error.response?.data?.message || "Failed to load dashboard",
          color: 'error',
          timeout: 5000
        });
      }
    },
    async handleDashboardApiResponse(dashboardData) {
      // Store the actual dashboard ID
      this.dashboardIdValue = dashboardData.ID || dashboardData.id || null;
      
      // Set dashboard name
      this.dashboardName = dashboardData.Name || "Untitled Dashboard";
      this.originalDashboardName = this.dashboardName; // Set original name
      this.dashboardAccessLevel = dashboardData.AccessLevel || "private";
      this.isDefault = dashboardData.isDefault || false;
      
      // Set AI response content and seed dashboard chat history (session-only)
      if (dashboardData.content) {
        this.aiResponse = dashboardData.content;
        this.ensureChatHistory("dashboard");
        if (this.initialMessage && !this.chatHistories.dashboard.some((m) => m.role === "user" && m.text === this.initialMessage)) {
          this.chatHistories.dashboard.push({ role: "user", text: this.initialMessage });
        }
        this.chatHistories.dashboard.push({ role: "ai", text: dashboardData.content });
      }

      // Load charts from ChartsID array
      if (dashboardData.ChartsID && dashboardData.ChartsID.length > 0) {
        this.loadingCharts = true;
        await this.loadChartsFromIds(dashboardData.ChartsID);
        this.loadingCharts = false;
      }
    },
    async loadChartsFromIds(chartIds) {
      // Reset charts array
      this.charts = Array(6).fill(null);
      
      // Load each chart by ID
      for (let i = 0; i < chartIds.length && i < 6; i++) {
        const chartId = chartIds[i];
        try {
          // First get chart metadata
          const chartResponse = await api.get(`public/api/charts/${chartId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          });

          if (
            chartResponse &&
            chartResponse.data &&
            (chartResponse.data.statusCode === 200 || chartResponse.data.statusCode === 201)
          ) {
            const chart = chartResponse.data.data?.chart || chartResponse.data.data;
            if (chart) {
              // Fetch chart data with filters
              try {
                // Build filter query parameters from chart Filter object
                const filterParams = this.buildFilterQueryParams(chart.Filter);
                const queryString = Object.entries(filterParams)
                  .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
                  .join("&");
                
                // Build URL with filter query parameters if any exist
                const url = queryString 
                  ? `public/api/charts/data/${chartId}?${queryString}`
                  : `public/api/charts/data/${chartId}`;
                
                const chartDataResponse = await api.get(url, {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    "X-TenantID": this.tenantId,
                  },
                });

                let chartData = null;
                if (
                  chartDataResponse &&
                  chartDataResponse.data &&
                  (chartDataResponse.data.statusCode === 200 || chartDataResponse.data.statusCode === 201)
                ) {
                  const samples = chartDataResponse.data.data?.samples;
                  
                  // Handle different sample formats
                  if (samples != null) {
                    if (Array.isArray(samples) && samples.length > 0) {
                      chartData = samples;
                    } else if (typeof samples === 'object' && 'rows' in samples && samples.rows) {
                      chartData = samples.rows;
                    }
                  }
                }

                this.charts[i] = {
                  title: chart.Name || `Chart ${i + 1}`,
                  data: chart,
                  chartData: chartData,
                  index: i,
                };
              } catch (dataError) {
                console.error(`Error loading chart data for ${chartId}:`, dataError);
                
                // Check for permission error (400 status with permission message)
                if (dataError.response?.data?.statusCode === 400 && 
                    dataError.response?.data?.message && 
                    dataError.response.data.message.includes("don't have permission")) {
                  const chartName = chart.Name || `Chart ${i + 1}`;
                  this.$store.commit('api/showSnackbar', {
                    message: `${chartName}: ${dataError.response.data.message}`,
                    color: 'error',
                    timeout: 5000
                  });
                }
                
                // Still add chart even if data fails
                this.charts[i] = {
                  title: chart.Name || `Chart ${i + 1}`,
                  data: chart,
                  chartData: null,
                  index: i,
                };
              }
            }
          }
        } catch (error) {
          console.error(`Error loading chart ${chartId}:`, error);
          
          // Check for permission error (400 status with permission message)
          if (error.response?.data?.statusCode === 400 && 
              error.response?.data?.message && 
              error.response.data.message.includes("don't have permission")) {
            // Try to get chart name from response or use default
            const chartName = error.response?.data?.chart?.Name || 
                            error.response?.data?.data?.chart?.Name || 
                            `Chart ${i + 1}`;
            this.$store.commit('api/showSnackbar', {
              message: `${chartName}: ${error.response.data.message}`,
              color: 'error',
              timeout: 5000
            });
          }
        }
      }
    },
    createDashboardFromMessage(message) {
      // Create a new dashboard from the message
      // This will be handled by the backend API
      console.log("Creating dashboard from message:", message);
    },
    handleChartAction(index) {
      // Handle "Edit in Studio" button click - open visual edit panel
      if (this.selectedChartIndex === null || this.selectedChartIndex !== index) {
        // First select the chart if not already selected
        this.selectedChartIndex = index;
      }
      // Open visual edit panel
      this.showVisualEditPanel = true;
      this.chatVisible = false; // Hide chat when showing visual edit
    },
    deselectPlot() {
      // Deselect the plot and return to grid view
      this.selectedChartIndex = null;
      this.showVisualEditPanel = false;
      this.chatVisible = true; // Show chat again
    },
    getChartTitle(index) {
      // Get chart title with fallback
      if (this.charts[index]) {
        return this.charts[index].title || 
               this.charts[index].data?.Name || 
               `Plot ${index + 1}`;
      }
      return `Plot ${index + 1}`;
    },
    handleMessageLike(index) {
      // Handle message like
      console.log("Message liked:", index);
    },
    handleMessageDislike(index) {
      // Handle message dislike
      console.log("Message disliked:", index);
    },
    async handleChatMessage(message) {
      // Handle new chat message with dashboard_id
      console.log("New chat message:", message);
      console.log("Current dashboard ID:", this.currentDashboardId);
      
      // Call the LLM API with message and dashboard_id
      const llmUrl = process.env.VUE_APP_LLM_URL;
      if (!llmUrl) {
        console.error("VUE_APP_LLM_URL is not defined in .env");
        this.$store.commit('api/showSnackbar', {
          message: "LLM URL is not configured",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      this.loadingChatMessage = true;
      const chatKey = this.currentChatKey;
      this.appendToChatHistory(chatKey, { role: "user", text: message }, null);
      try {
        // Check if a plot is selected for edit (when selectedChartIndex is not null, use new API)
        if (this.selectedChartIndex !== null && this.charts[this.selectedChartIndex]) {
          // Use new API for visual edit mode
          const chart = this.charts[this.selectedChartIndex];
          const chartMetadata = chart.data || chart;
          const chartId = chartMetadata.ID;

          if (!chartId) {
            this.$store.commit('api/showSnackbar', {
              message: "Cannot update: Chart ID not found",
              color: 'error',
              timeout: 5000
            });
            this.loadingChatMessage = false;
            return;
          }

          // Build payload in the required format for LLM API
          const llmPayload = {
            data: {
              chart: {
                AccessLevel: chartMetadata.AccessLevel || "public",
                ChartType: chartMetadata.ChartType || "",
                CreatedBy: chartMetadata.CreatedBy || "",
                CreatedOn: chartMetadata.CreatedOn || new Date().toISOString(),
                Filter: chartMetadata.Filter || {},
                GroupColumn: chartMetadata.GroupColumn || "",
                ID: chartId,
                Name: chartMetadata.Name || "",
                TableType: chartMetadata.TableType || "",
                XAxisColumn: chartMetadata.XAxisColumn || "",
                XAxisScale: chartMetadata.XAxisScale || "linear",
                YAxisColumn: chartMetadata.YAxisColumn || "",
                YAxisScale: chartMetadata.YAxisScale || "linear",
                ZAxisColumn: chartMetadata.ZAxisColumn || "",
              }
            },
            page: "VisualEdit",
            message: message
          };

          const response = await api.post(
            `${llmUrl}api/updateChartInDashboard`,
            llmPayload,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                "X-TenantID": this.tenantId,
              },
            }
          );

          if (response && response.data) {
            console.log("Visual Edit API response:", response.data);
            
            // Update AI response - check multiple possible response structures
            let content = null;
            if (response.data.content) {
              content = response.data.content;
            } else if (response.data.data && response.data.data.content) {
              content = response.data.data.content;
            } else if (response.data.message) {
              content = response.data.message;
            } else if (typeof response.data === 'string') {
              content = response.data;
            }
            
            if (content) {
              console.log("Setting AI response:", content);
              this.aiResponse = content;
              this.appendToChatHistory(chatKey, null, { role: "ai", text: content });
            } else {
              console.warn("No content found in API response:", response.data);
            }

            // Check if metadata.route = "chartEditAgent" - if so, reload chart from API
            if (response.data.metadata && response.data.metadata.route === "chartEditAgent") {
              console.log("ChartEditAgent route detected, reloading chart from API");
              // Reload the chart metadata and data from API
              await this.reloadSingleChart(this.selectedChartIndex);
            } else if (response.data.data && response.data.data.chart) {
              // If the response contains updated chart data, reload it
              const updatedChartMetadata = response.data.data.chart;
              this.charts[this.selectedChartIndex] = {
                ...this.charts[this.selectedChartIndex],
                data: updatedChartMetadata,
              };
              // Reload chart data
              await this.reloadChartData(this.selectedChartIndex);
            }

            this.$store.commit('api/showSnackbar', {
              message: "Plot updated successfully",
              color: 'success',
              timeout: 3000
            });
          }
        } else {
          // Use old API for normal chat mode
          // Get current charts IDs from the charts array
          const chartsID = this.charts
            .filter(chart => chart && chart.data && chart.data.ID)
            .map(chart => chart.data.ID);

          // Build full dashboard data object
          const dashboardData = {
            ID: this.currentDashboardId,
            Name: this.dashboardName,
            ChartsID: chartsID,
            AccessLevel: this.dashboardAccessLevel,
            isDefault: this.isDefault,
          };

          const requestPayload = {
            message: message,
          };

          // Add full dashboard data instead of just dashboard_id
          if (this.currentDashboardId) {
            requestPayload.dashboard = dashboardData;
          }

          const response = await api.post(
            `${llmUrl}api/dashboard`,
            requestPayload,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                "X-TenantID": this.tenantId,
              },
            }
          );

          if (response && response.data) {
            const dashboardData = response.data;
            
            console.log("Dashboard API response:", dashboardData);
            
            // Update AI response - check multiple possible response structures
            let content = null;
            if (dashboardData.content) {
              content = dashboardData.content;
            } else if (dashboardData.data && dashboardData.data.content) {
              content = dashboardData.data.content;
            } else if (dashboardData.message) {
              content = dashboardData.message;
            } else if (typeof dashboardData === 'string') {
              content = dashboardData;
            }
            
            if (content) {
              console.log("Setting AI response:", content);
              this.aiResponse = content;
              this.appendToChatHistory(chatKey, null, { role: "ai", text: content });
            } else {
              console.warn("No content found in API response:", dashboardData);
            }

            // Update charts if new charts are provided
            if (dashboardData.ChartsID && dashboardData.ChartsID.length > 0) {
              this.loadingCharts = true;
              await this.loadChartsFromIds(dashboardData.ChartsID);
              this.loadingCharts = false;
              // Show success message when charts are updated
              this.$store.commit('api/showSnackbar', {
                message: "Dashboard updated successfully",
                color: 'success',
                timeout: 3000
              });
            }

            // Update dashboard name if provided
            if (dashboardData.Name) {
              this.dashboardName = dashboardData.Name;
              this.originalDashboardName = dashboardData.Name; // Update original name
              // Update URL with new dashboard name
              // Vue Router will handle encoding automatically
              this.$router.replace({
                name: "AgenticDashboardView",
                params: { dashboardName: this.dashboardName },
              });
            }
          } else {
            console.error("Invalid response from API");
            this.$store.commit('api/showSnackbar', {
              message: "Failed to process message",
              color: 'error',
              timeout: 5000
            });
          }
        }
      } catch (error) {
        console.error("Error sending chat message:", error);
        
        // Get plot name for error message if in edit mode
        let errorMessage = error.response?.data?.message || "Failed to send message";
        if (this.selectedChartIndex !== null && this.charts[this.selectedChartIndex]) {
          const chart = this.charts[this.selectedChartIndex];
          const chartMetadata = chart.data || chart;
          const plotName = chartMetadata.Name || "Plot";
          
          // Check for permission error (400 status with permission message)
          if (error.response?.data?.statusCode === 400 && 
              error.response?.data?.message && 
              error.response.data.message.includes("don't have permission")) {
            errorMessage = `${plotName}: ${error.response.data.message}`;
          } else {
            errorMessage = `${plotName}: ${errorMessage}`;
          }
        }
        
        this.$store.commit('api/showSnackbar', {
          message: errorMessage,
          color: 'error',
          timeout: 5000
        });
      } finally {
        this.loadingChatMessage = false;
      }
    },
    handleDashboardNameChange() {
      // Handle dashboard name change
      console.log("Dashboard name changed:", this.dashboardName);
      // Save button will be enabled automatically via isDashboardNameChanged computed property
    },
    toggleChat() {
      // Toggle chat sidebar visibility
      this.chatVisible = !this.chatVisible;
    },
    ensureChatHistory(key) {
      if (!key) return;
      if (!Array.isArray(this.chatHistories[key])) {
        this.chatHistories = { ...this.chatHistories, [key]: [] };
      }
    },
    appendToChatHistory(key, userMsg, aiMsg) {
      if (!key) return;
      this.ensureChatHistory(key);
      const list = this.chatHistories[key];
      if (userMsg) list.push(userMsg);
      if (aiMsg) list.push(aiMsg);
    },
    resizeAllCharts() {
      // Resize all Plotly charts when container dimensions change (e.g. chat hidden/shown)
      this.$nextTick(() => {
        const chartContainers = document.querySelectorAll('.chart-container');
        chartContainers.forEach((container) => {
          const plotElement = container.querySelector('.js-plotly-plot') ||
                             (container.classList.contains('js-plotly-plot') ? container : null);
          if (plotElement) {
            try {
              Plotly.Plots.resize(plotElement)
                .then(() => {
                  // Force relayout so axis/scale positions update (fixes gap between scale and y-axis after sidebar toggle)
                  return Plotly.relayout(plotElement, {});
                })
                .catch((err) => {
                  console.warn("Error resizing chart:", err);
                });
            } catch (error) {
              console.warn("Error resizing chart:", error);
            }
          }
        });
      });
    },
    handleEmptySlotClick() {
      // If chat is closed, open it
      if (!this.chatVisible) {
        this.chatVisible = true;
        // Wait for chat to open and then focus input
        this.$nextTick(() => {
          setTimeout(() => {
            this.focusChatInput();
          }, 350); // Wait for CSS transition
        });
      } else {
        // If chat is open, focus the input field
        this.focusChatInput();
      }
    },
    focusChatInput() {
      // Focus the chat input field
      if (this.$refs.chatSidebar) {
        this.$refs.chatSidebar.focusInput();
      }
    },
    handleChartSelected(index) {
      // Select chart - load it on the left screen
      if (this.selectedChartIndex === index) {
        // Deselect if clicking the same chart
        this.selectedChartIndex = null;
        this.showVisualEditPanel = false;
        this.chatVisible = true;
      } else {
        this.selectedChartIndex = index;
        // Keep chat visible to show visual controls
        this.chatVisible = true;
        this.showVisualEditPanel = false;
      }
    },
    handleVisualEdit() {
      if (this.selectedChartIndex === null || !this.charts[this.selectedChartIndex]) {
        // No plot selected - show message
        this.$store.commit('api/showSnackbar', {
          message: "Please select a plot first",
          color: 'warning',
          timeout: 3000
        });
        this.showVisualEditPanel = false;
        this.editMode = false;
      } else {
        // Plot selected - show visual edit panel (replaces chat)
        this.showVisualEditPanel = true;
        this.chatVisible = false; // Hide chat when showing visual edit
      }
    },
    switchToChat() {
      // Switch from visual edit back to chat
      this.showVisualEditPanel = false;
      this.chatVisible = true;
    },
    exitEditMode() {
      // Exit visual edit and return to chat/dashboard view
      this.showVisualEditPanel = false;
      this.chatVisible = true; // Show chat again
      // Optionally deselect the chart
      // this.selectedChartIndex = null;
    },
    async handlePlotUpdated(updateData) {
      if (this.selectedChartIndex === null || !this.charts[this.selectedChartIndex]) {
        return;
      }

      const chart = this.charts[this.selectedChartIndex];
      const chartMetadata = chart.data || chart;
      const chartId = chartMetadata.ID;

      if (!chartId) {
        this.$store.commit('api/showSnackbar', {
          message: "Cannot update: Chart ID not found",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      try {
        // STEP 1: Update chart metadata locally IMMEDIATELY for real-time preview
        // This ensures the plot updates in real-time before API call
        // Get existing YAxisColumn first to preserve it
        const normalizeYAxisColumn = (value) => {
          if (!value) return "";
          if (Array.isArray(value)) {
            return value.filter(col => col && col.trim()).join(",");
          }
          if (typeof value === 'string') {
            return value.trim();
          }
          return String(value);
        };
        
        const existingYAxisColumn = normalizeYAxisColumn(
          chartMetadata.YAxisColumn || 
          (Array.isArray(chartMetadata.YAxisColumns) ? chartMetadata.YAxisColumns : null)
        );
        
        // Build local update - preserve ALL existing data, only update what's changed
        // Start with deep copy of all existing chart metadata
        const localUpdatedMetadata = {
          ...chartMetadata, // Preserve ALL existing fields
          ID: chartId, // Always preserve chart ID
        };
        
        // Only override with updateData values that are explicitly provided
        if (updateData.Name !== undefined && updateData.Name !== null) {
          localUpdatedMetadata.Name = updateData.Name;
        }
        if (updateData.TableType !== undefined && updateData.TableType !== null) {
          localUpdatedMetadata.TableType = updateData.TableType;
        }
        if (updateData.ChartType !== undefined && updateData.ChartType !== null) {
          localUpdatedMetadata.ChartType = updateData.ChartType;
        }
        if (updateData.XAxisColumn !== undefined && updateData.XAxisColumn !== null) {
          localUpdatedMetadata.XAxisColumn = updateData.XAxisColumn;
        }
        // Always use YAxisColumn from updateData if provided (user might want to change it)
        // Only preserve existing if updateData doesn't have YAxisColumn at all
        if (updateData.YAxisColumn !== undefined) {
          // User explicitly provided YAxisColumn (even if empty string - they might want to clear it)
          localUpdatedMetadata.YAxisColumn = updateData.YAxisColumn || ""; // Allow empty string
        } else {
          // YAxisColumn not in updateData - preserve existing
          localUpdatedMetadata.YAxisColumn = existingYAxisColumn;
        }
        if (updateData.ZAxisColumn !== undefined && updateData.ZAxisColumn !== null) {
          localUpdatedMetadata.ZAxisColumn = updateData.ZAxisColumn;
        }
        if (updateData.GroupColumn !== undefined && updateData.GroupColumn !== null) {
          localUpdatedMetadata.GroupColumn = updateData.GroupColumn;
        }
        if (updateData.XAxisScale !== undefined && updateData.XAxisScale !== null) {
          localUpdatedMetadata.XAxisScale = updateData.XAxisScale;
        }
        if (updateData.YAxisScale !== undefined && updateData.YAxisScale !== null) {
          localUpdatedMetadata.YAxisScale = updateData.YAxisScale;
        }
        // Preserve Filter - use the filter from updateData (it already has existing values merged)
        // The VisualEditsPanel already merges existing filter with current selections
        if (updateData.Filter !== undefined && updateData.Filter !== null) {
          localUpdatedMetadata.Filter = updateData.Filter;
        } else {
          // If no filter in updateData, preserve existing filter completely
          localUpdatedMetadata.Filter = chartMetadata.Filter || {};
        }
        // Preserve all other optional fields from existing metadata
        if (chartMetadata.NumXBins !== undefined) {
          localUpdatedMetadata.NumXBins = chartMetadata.NumXBins;
        }
        if (chartMetadata.NumYBins !== undefined) {
          localUpdatedMetadata.NumYBins = chartMetadata.NumYBins;
        }
        if (chartMetadata.AccessLevel !== undefined) {
          localUpdatedMetadata.AccessLevel = chartMetadata.AccessLevel;
        }
        if (chartMetadata.CreatedBy !== undefined) {
          localUpdatedMetadata.CreatedBy = chartMetadata.CreatedBy;
        }
        if (chartMetadata.CreatedOn !== undefined) {
          localUpdatedMetadata.CreatedOn = chartMetadata.CreatedOn;
        }
        if (chartMetadata.UpdatedBy !== undefined) {
          localUpdatedMetadata.UpdatedBy = chartMetadata.UpdatedBy;
        }
        if (chartMetadata.UpdatedOn !== undefined) {
          localUpdatedMetadata.UpdatedOn = chartMetadata.UpdatedOn;
        }
        
        // Check if metadata changed in a way that requires data reload
        // (axis columns, filters, table type, chart type changes require new data)
        const requiresDataReload = 
          (updateData.XAxisColumn !== undefined && updateData.XAxisColumn !== chartMetadata.XAxisColumn) ||
          (updateData.YAxisColumn !== undefined && updateData.YAxisColumn !== existingYAxisColumn) ||
          (updateData.ZAxisColumn !== undefined && updateData.ZAxisColumn !== chartMetadata.ZAxisColumn) ||
          (updateData.GroupColumn !== undefined && updateData.GroupColumn !== chartMetadata.GroupColumn) ||
          (updateData.TableType !== undefined && updateData.TableType !== chartMetadata.TableType) ||
          (updateData.ChartType !== undefined && updateData.ChartType !== chartMetadata.ChartType) ||
          (updateData.Filter !== undefined && updateData.Filter !== null);
        
        // Update the chart in the array immediately for real-time preview
        // Create a new object to trigger Vue reactivity
        this.charts[this.selectedChartIndex] = {
          ...this.charts[this.selectedChartIndex],
          data: { ...localUpdatedMetadata }, // Create new object to trigger watcher
          // If metadata changed significantly, clear chartData to force reload with new metadata
          // Otherwise preserve existing chartData for smooth updates
          chartData: requiresDataReload ? null : this.charts[this.selectedChartIndex].chartData,
          title: updateData.Name || chart.title || `Chart ${this.selectedChartIndex + 1}`,
        };
        
        // If only name changed, we can update immediately without reloading data
        // For other changes, ChartCard watcher will detect missing chartData and reload
        if (!requiresDataReload) {
          // Only name changed - just update the title, don't reload plot
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
        
        console.log("Chart updated locally for real-time preview:", {
          localUpdatedMetadata,
          existingYAxisColumn,
          updateDataYAxisColumn: updateData.YAxisColumn,
          finalYAxisColumn: localUpdatedMetadata.YAxisColumn
        });

        // STEP 2: Prepare update payload for API - merge existing chart metadata with updates
        const mergedChartData = {
          ...chartMetadata,
          ...updateData,
        };

        console.log("Update data received:", updateData);
        console.log("Chart metadata:", chartMetadata);
        console.log("Existing YAxisColumn from metadata:", {
          YAxisColumn: chartMetadata.YAxisColumn,
          YAxisColumns: chartMetadata.YAxisColumns,
          existingYAxisColumn: existingYAxisColumn
        });

        // Wrap payload in data parameter as required by API
        // Start with ALL existing chart metadata as base - preserve ALL existing values
        // Only override with updateData values that are actually provided and different
        const chartPayload = {
          ...chartMetadata, // Start with ALL existing fields - preserves everything
          ID: chartId, // Always ensure ID is set
        };
        
        // Initialize with defaults only if fields don't exist
        if (!chartPayload.AccessLevel) chartPayload.AccessLevel = "public";
        if (!chartPayload.ChartType) chartPayload.ChartType = "";
        if (!chartPayload.CreatedBy) chartPayload.CreatedBy = "";
        if (!chartPayload.CreatedOn) chartPayload.CreatedOn = new Date().toISOString();
        if (!chartPayload.Filter) chartPayload.Filter = {};
        if (!chartPayload.GroupColumn) chartPayload.GroupColumn = "";
        if (!chartPayload.Name) chartPayload.Name = "";
        if (!chartPayload.TableType) chartPayload.TableType = "";
        if (!chartPayload.XAxisColumn) chartPayload.XAxisColumn = "";
        if (!chartPayload.XAxisScale) chartPayload.XAxisScale = "linear";
        if (!chartPayload.YAxisColumn) chartPayload.YAxisColumn = existingYAxisColumn;
        if (!chartPayload.YAxisScale) chartPayload.YAxisScale = "linear";
        if (!chartPayload.ZAxisColumn) chartPayload.ZAxisColumn = "";

        // Only override with updateData values if they are provided and different from existing
        // This prevents wiping out data when only one field is changed
        if (updateData.Name !== undefined && updateData.Name !== null && updateData.Name !== chartPayload.Name) {
          chartPayload.Name = updateData.Name;
        }
        if (updateData.TableType !== undefined && updateData.TableType !== null && updateData.TableType !== chartPayload.TableType) {
          chartPayload.TableType = updateData.TableType;
        }
        if (updateData.ChartType !== undefined && updateData.ChartType !== null && updateData.ChartType !== chartPayload.ChartType) {
          chartPayload.ChartType = updateData.ChartType;
        }
        if (updateData.XAxisColumn !== undefined && updateData.XAxisColumn !== null && updateData.XAxisColumn !== chartPayload.XAxisColumn) {
          chartPayload.XAxisColumn = updateData.XAxisColumn;
        }
        // For YAxisColumn, always use updateData if provided (user explicitly changed it)
        // Only preserve existing if updateData doesn't have YAxisColumn at all
        if (updateData.YAxisColumn !== undefined) {
          // User explicitly provided YAxisColumn - use it (even if empty string)
          const normalizedUpdateYAxis = normalizeYAxisColumn(updateData.YAxisColumn);
          chartPayload.YAxisColumn = normalizedUpdateYAxis;
          console.log("Updating YAxisColumn to:", normalizedUpdateYAxis);
        } else {
          // YAxisColumn not in updateData - preserve existing value
          console.log("YAxisColumn not in updateData - preserving existing:", existingYAxisColumn);
          // chartPayload.YAxisColumn already has existingYAxisColumn, so no change needed
        }
        if (updateData.ZAxisColumn !== undefined && updateData.ZAxisColumn !== null && updateData.ZAxisColumn !== chartPayload.ZAxisColumn) {
          chartPayload.ZAxisColumn = updateData.ZAxisColumn;
        }
        if (updateData.GroupColumn !== undefined && updateData.GroupColumn !== null && updateData.GroupColumn !== chartPayload.GroupColumn) {
          chartPayload.GroupColumn = updateData.GroupColumn;
        }
        if (updateData.XAxisScale !== undefined && updateData.XAxisScale !== null && updateData.XAxisScale !== chartPayload.XAxisScale) {
          chartPayload.XAxisScale = updateData.XAxisScale;
        }
        if (updateData.YAxisScale !== undefined && updateData.YAxisScale !== null && updateData.YAxisScale !== chartPayload.YAxisScale) {
          chartPayload.YAxisScale = updateData.YAxisScale;
        }
        // For Filter, use the filter from updateData (it already has existing values merged)
        // The VisualEditsPanel already merges existing filter with current selections
        if (updateData.Filter !== undefined && updateData.Filter !== null) {
          chartPayload.Filter = updateData.Filter;
        } else {
          // If no filter in updateData, preserve existing filter completely
          chartPayload.Filter = chartMetadata.Filter || {};
        }
        if (updateData.AccessLevel !== undefined && updateData.AccessLevel !== null && updateData.AccessLevel !== chartPayload.AccessLevel) {
          chartPayload.AccessLevel = updateData.AccessLevel;
        }

        // Preserve all optional fields from existing metadata (they're already in chartPayload from spread)
        // Only update if explicitly provided in updateData
        if (updateData.NumXBins !== undefined && updateData.NumXBins !== null) {
          chartPayload.NumXBins = updateData.NumXBins;
        }
        if (updateData.NumYBins !== undefined && updateData.NumYBins !== null) {
          chartPayload.NumYBins = updateData.NumYBins;
        }
        // Preserve AccessLevel if not explicitly changed
        if (updateData.AccessLevel !== undefined && updateData.AccessLevel !== null) {
          chartPayload.AccessLevel = updateData.AccessLevel;
        }

        console.log("Chart payload being sent:", chartPayload);
        console.log("YAxisColumn in payload:", {
          existingYAxisColumn: existingYAxisColumn,
          updateDataYAxisColumn: updateData.YAxisColumn,
          finalYAxisColumn: chartPayload.YAxisColumn
        });

        const updatePayload = {
          data: {
            chart: chartPayload
          }
        };

        console.log("Full update payload:", updatePayload);

        // Call regular API to update the chart (NO LLM API call here - only for input changes)
        const response = await api.put(`public/api/charts/${chartId}`, updatePayload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });

        console.log("API response:", response.data);

        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          // STEP 3: Update with API response (may have additional fields from server)
          const updatedChartMetadata = response.data.data?.chart || response.data.data;
          console.log("Updated chart metadata from API:", updatedChartMetadata);
          
          // Ensure chart ID is preserved (critical!)
          if (!updatedChartMetadata.ID) {
            updatedChartMetadata.ID = chartId;
          }
          
          // Preserve ALL existing fields that might be missing from API response
          // Merge API response with existing metadata to ensure nothing is lost
          const finalMetadata = {
            ...chartMetadata, // Start with ALL existing metadata
            ...updatedChartMetadata, // Override with API response
            ID: chartId, // Always ensure ID
          };
          
          // YAxisColumn: if user sent an update in this request, always use what we sent (never revert to old).
          // Only fill from existing when user did NOT change Y-axis and API didn't return it.
          if (updateData.YAxisColumn !== undefined) {
            // User explicitly updated Y-axis (including removing one or more) - use the value we sent
            const sentYAxis = normalizeYAxisColumn(updateData.YAxisColumn);
            finalMetadata.YAxisColumn = sentYAxis !== undefined && sentYAxis !== null ? sentYAxis : "";
          } else if (!finalMetadata.YAxisColumn && existingYAxisColumn) {
            // User did not change Y-axis - preserve existing if API didn't return it
            finalMetadata.YAxisColumn = existingYAxisColumn;
          }
          
          // Ensure Filter is preserved if it was in the original metadata
          if (!finalMetadata.Filter && chartMetadata.Filter) {
            finalMetadata.Filter = chartMetadata.Filter;
          }
          
          // Preserve all axis columns if they exist in original
          if (!finalMetadata.XAxisColumn && chartMetadata.XAxisColumn) {
            finalMetadata.XAxisColumn = chartMetadata.XAxisColumn;
          }
          if (!finalMetadata.ZAxisColumn && chartMetadata.ZAxisColumn) {
            finalMetadata.ZAxisColumn = chartMetadata.ZAxisColumn;
          }
          if (!finalMetadata.GroupColumn && chartMetadata.GroupColumn) {
            finalMetadata.GroupColumn = chartMetadata.GroupColumn;
          }
          
          // Preserve optional fields
          if (chartMetadata.NumXBins !== undefined && finalMetadata.NumXBins === undefined) {
            finalMetadata.NumXBins = chartMetadata.NumXBins;
          }
          if (chartMetadata.NumYBins !== undefined && finalMetadata.NumYBins === undefined) {
            finalMetadata.NumYBins = chartMetadata.NumYBins;
          }
          
          // Update the chart in the charts array with merged metadata
          this.charts[this.selectedChartIndex] = {
            ...this.charts[this.selectedChartIndex],
            data: finalMetadata,
            title: finalMetadata.Name || this.charts[this.selectedChartIndex].title,
          };

          // STEP 4: Reload chart data with filters to reflect changes
          await this.reloadChartData(this.selectedChartIndex);
          
          console.log("Chart updated and data reloaded");

          this.$store.commit('api/showSnackbar', {
            message: "Plot updated successfully",
            color: 'success',
            timeout: 3000
          });
        }
      } catch (error) {
        console.error("Error updating plot:", error);
        
        // Get plot name for error message
        const plotName = updateData.Name || chartMetadata.Name || "Plot";
        
        // Check for permission error (400 status with permission message)
        if (error.response?.data?.statusCode === 400 && 
            error.response?.data?.message && 
            error.response.data.message.includes("don't have permission")) {
          this.$store.commit('api/showSnackbar', {
            message: `${plotName}: ${error.response.data.message}`,
            color: 'error',
            timeout: 5000
          });
        } else {
          this.$store.commit('api/showSnackbar', {
            message: `${plotName}: ${error.response?.data?.message || "Failed to update plot"}`,
            color: 'error',
            timeout: 5000
          });
        }
      }
    },
    async reloadSingleChart(chartIndex) {
      // Reload a single chart from API (metadata + data) without refreshing other data
      const chart = this.charts[chartIndex];
      if (!chart) return;
      
      const chartMetadata = chart.data || chart;
      const chartId = chartMetadata.ID;

      if (!chartId) {
        console.warn("Cannot reload chart: Chart ID not found");
        return;
      }

      try {
        // Get updated chart metadata
        const chartResponse = await api.get(`public/api/charts/${chartId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });

        if (
          chartResponse &&
          chartResponse.data &&
          (chartResponse.data.statusCode === 200 || chartResponse.data.statusCode === 201)
        ) {
          const updatedChart = chartResponse.data.data?.chart || chartResponse.data.data;
          
          // Get updated chart data with filters
          try {
            // Build filter query parameters from chart Filter object
            const filterParams = this.buildFilterQueryParams(updatedChart.Filter);
            const queryString = Object.entries(filterParams)
              .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
              .join("&");
            
            // Build URL with filter query parameters if any exist
            const url = queryString 
              ? `public/api/charts/data/${chartId}?${queryString}`
              : `public/api/charts/data/${chartId}`;
            
            const chartDataResponse = await api.get(url, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                "X-TenantID": this.tenantId,
              },
            });

            let chartData = null;
            if (
              chartDataResponse &&
              chartDataResponse.data &&
              (chartDataResponse.data.statusCode === 200 || chartDataResponse.data.statusCode === 201)
            ) {
              const samples = chartDataResponse.data.data?.samples;
              
              if (samples != null) {
                if (Array.isArray(samples)) {
                  chartData = samples;
                } else if (typeof samples === 'object' && 'rows' in samples && samples.rows) {
                  chartData = samples.rows;
                }
              }
            }

            // Update only this chart in the array - this will trigger re-render
            this.charts[chartIndex] = {
              title: updatedChart.Name || chart.title || `Chart ${chartIndex + 1}`,
              data: updatedChart,
              chartData: chartData,
              index: chartIndex,
            };

            console.log("Chart reloaded successfully:", {
              chartIndex,
              chartId,
              chartName: updatedChart.Name
            });
          } catch (dataError) {
            console.error("Error reloading chart data:", dataError);
            // Still update metadata even if data fails
            this.charts[chartIndex] = {
              ...this.charts[chartIndex],
              data: updatedChart,
            };
          }
        }
      } catch (error) {
        console.error("Error reloading single chart:", error);
        
        const plotName = chartMetadata?.Name || "Plot";
        
        // Check for permission error
        if (error.response?.data?.statusCode === 400 && 
            error.response?.data?.message && 
            error.response.data.message.includes("don't have permission")) {
          this.$store.commit('api/showSnackbar', {
            message: `${plotName}: ${error.response.data.message}`,
            color: 'error',
            timeout: 5000
          });
        }
      }
    },
    // Helper method to build filter query parameters from chart Filter object
    buildFilterQueryParams(chartFilter) {
      const queryParams = {};
      
      if (!chartFilter) return queryParams;
      
      // Only add filters to params if they have values
      if (chartFilter.asset && Array.isArray(chartFilter.asset) && chartFilter.asset.length) {
        queryParams.asset = JSON.stringify(chartFilter.asset);
      }
      if (chartFilter.field && Array.isArray(chartFilter.field) && chartFilter.field.length) {
        queryParams.field = JSON.stringify(chartFilter.field);
      }
      if (chartFilter.well && Array.isArray(chartFilter.well) && chartFilter.well.length) {
        queryParams.well = JSON.stringify(chartFilter.well);
      }
      if (chartFilter.reservoir && Array.isArray(chartFilter.reservoir) && chartFilter.reservoir.length) {
        queryParams.reservoir = JSON.stringify(chartFilter.reservoir);
      }
      if (chartFilter.fluidType && Array.isArray(chartFilter.fluidType) && chartFilter.fluidType.length) {
        queryParams.fluidType = JSON.stringify(chartFilter.fluidType);
      }
      if (chartFilter.sampleType && Array.isArray(chartFilter.sampleType) && chartFilter.sampleType.length) {
        queryParams.sampleType = JSON.stringify(chartFilter.sampleType);
      }
      if (chartFilter.fluidClassification && Array.isArray(chartFilter.fluidClassification) && chartFilter.fluidClassification.length) {
        queryParams.fluidClassification = JSON.stringify(chartFilter.fluidClassification);
      }
      if (chartFilter.fluidSampleID && Array.isArray(chartFilter.fluidSampleID) && chartFilter.fluidSampleID.length) {
        queryParams.fluidSampleID = JSON.stringify(chartFilter.fluidSampleID);
      }
      if (chartFilter.fluidSampleContainerID && Array.isArray(chartFilter.fluidSampleContainerID) && chartFilter.fluidSampleContainerID.length) {
        queryParams.fluidSampleContainerID = JSON.stringify(chartFilter.fluidSampleContainerID);
      }
      if (chartFilter.source && chartFilter.source !== "") {
        queryParams.source = `"${chartFilter.source}"`;
      }
      if (chartFilter.depth) {
        queryParams.depth = JSON.stringify(chartFilter.depth);
      }
      if (chartFilter.formationPressure) {
        queryParams.formationPressure = JSON.stringify(chartFilter.formationPressure);
      }
      if (chartFilter.formationTemperature) {
        queryParams.formationTemperature = JSON.stringify(chartFilter.formationTemperature);
      }
      if (chartFilter.sampleDate) {
        queryParams.sampleDate = JSON.stringify(chartFilter.sampleDate);
      }
      
      return queryParams;
    },
    async reloadChartData(chartIndex) {
      const chart = this.charts[chartIndex];
      const chartMetadata = chart.data || chart;
      const chartId = chartMetadata.ID;

      if (!chartId) return;

      try {
        // Build filter query parameters from chart Filter object
        const filterParams = this.buildFilterQueryParams(chartMetadata.Filter);
        const queryString = Object.entries(filterParams)
          .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
          .join("&");
        
        // Build URL with filter query parameters if any exist
        const url = queryString 
          ? `public/api/charts/data/${chartId}?${queryString}`
          : `public/api/charts/data/${chartId}`;
        
        const chartDataResponse = await api.get(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });

        if (chartDataResponse && chartDataResponse.data && 
            (chartDataResponse.data.statusCode === 200 || chartDataResponse.data.statusCode === 201)) {
          const samples = chartDataResponse.data.data?.samples;
          let chartData = null;
          
          if (samples != null) {
            if (Array.isArray(samples)) {
              chartData = samples;
            } else if (typeof samples === 'object' && 'rows' in samples && samples.rows) {
              chartData = samples.rows;
            }
          }

          // Update chart data
          this.charts[chartIndex] = {
            ...this.charts[chartIndex],
            chartData: chartData,
          };
        }
      } catch (error) {
        console.error("Error reloading chart data:", error);
        
        // Get plot name for error message
        const chart = this.charts[chartIndex];
        const chartMetadata = chart?.data || chart;
        const plotName = chartMetadata?.Name || "Plot";
        
        // Check for permission error (400 status with permission message)
        if (error.response?.data?.statusCode === 400 && 
            error.response?.data?.message && 
            error.response.data.message.includes("don't have permission")) {
          this.$store.commit('api/showSnackbar', {
            message: `${plotName}: ${error.response.data.message}`,
            color: 'error',
            timeout: 5000
          });
        }
      }
    },
    async saveDashboard() {
      if (!this.currentDashboardId) {
        this.$store.commit('api/showSnackbar', {
          message: "Cannot save: Dashboard ID not found",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      if (!this.isDashboardNameChanged) {
        return; // No changes to save
      }

      this.saving = true;

      try {
        // Get current charts IDs from the charts array
        const chartsID = this.charts
          .filter(chart => chart && chart.data && chart.data.ID)
          .map(chart => chart.data.ID);

        const requestBody = {
          Data: {
            dashboard: {
              name: this.dashboardName.trim(),
              chartsID: chartsID,
              AccessLevel: this.dashboardAccessLevel,
              isDefault: this.isDefault,
            },
          },
        };

        const response = await api.put(
          `public/api/dashboards/${this.currentDashboardId}`,
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          }
        );

        if (
          response &&
          response.data &&
          (response.data.statusCode === 200 || response.data.statusCode === 201)
        ) {
          // Update original name to reflect saved state
          this.originalDashboardName = this.dashboardName.trim();
          
          // Update URL with new dashboard name
          // Vue Router will handle encoding automatically
          this.$router.replace({
            name: "AgenticDashboardView",
            params: { dashboardName: this.dashboardName.trim() },
          });
          
          this.$store.commit('api/showSnackbar', {
            message: "Dashboard updated successfully",
            color: 'success',
            timeout: 3000
          });
        } else {
          const errorMessage = response?.data?.message || "Failed to update dashboard";
          console.error(errorMessage);
          this.$store.commit('api/showSnackbar', {
            message: errorMessage,
            color: 'error',
            timeout: 5000
          });
        }
      } catch (error) {
        console.error("Error updating dashboard:", error);
        this.$store.commit('api/showSnackbar', {
          message: error.response?.data?.message || "Error updating dashboard",
          color: 'error',
          timeout: 5000
        });
      } finally {
        this.saving = false;
      }
    },
    confirmDelete() {
      if (!this.dashboardId) {
        // If no dashboard ID, just navigate back
        this.$router.push({
          name: "AgenticDashboard",
        });
        return;
      }

      this.deleting = true;
      api
        .delete(`public/api/dashboards/${this.dashboardId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        })
        .then((response) => {
          if (
            response &&
            response.data &&
            (response.data.statusCode === 200 || response.data.statusCode === 201)
          ) {
            this.$store.commit('api/showSnackbar', {
              message: "Dashboard deleted successfully",
              color: 'success',
              timeout: 3000
            });
            // Navigate back to dashboard list
            this.$router.push({
              name: "AgenticDashboard",
            });
          } else {
            this.$store.commit('api/showSnackbar', {
              message: response?.data?.message || "Failed to delete dashboard",
              color: 'error',
              timeout: 5000
            });
          }
        })
        .catch((error) => {
          console.error("Error deleting dashboard:", error);
          this.$store.commit('api/showSnackbar', {
            message: error.response?.data?.message || "Error deleting dashboard",
            color: 'error',
            timeout: 5000
          });
        })
        .finally(() => {
          this.deleting = false;
          this.showDeleteDialog = false;
        });
    },
  },
};
</script>

<style scoped>
@import '../../assets/Styling/agenticDashboard.css';
</style>

